import {SectionPanelSorting} from '../../../components/PanelBank/types';
import {setInShallowClone} from '../../../util/utility';
import * as PanelTypes from '../panel/types';
import * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {ActionType, ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {
  ActiveData,
  getMovePanelData,
  getUndoData,
  handleMovePanelToDiffSection,
  handleMovePanelToSameSection,
  TargetData,
} from './movePanelUtils';
import * as Types from './types';

/**
 * This actions handles moving the active panel to the target section's position in the array.
 * This reorders the panelRefs in the panelBankSectionConfig. This assumes you're moving the
 * panel WITHIN THE SAME SECTION. Please use movePanelToDiffSection for moving a panel to a different section.
 *
 * @param ref This is the panel bank config ref
 * @param active This is the panel that is being moved
 * @param target This is the info that indicates what position the active panel will be moved to and which section
 */
export const movePanelAction = (
  state: ViewReducerState,
  ref: Types.Ref,
  active: {
    panelRef: PanelTypes.Ref;
    sectionRef: PanelBankSectionConfigTypes.Ref;
  },
  target: {
    panelRef: PanelTypes.Ref | null; // a section can be empty, so the panelRef can be null
    sectionRef: PanelBankSectionConfigTypes.Ref;
    insertDir: 'before' | 'after'; // This is the direction the panel will be inserted in the target section
  }
): [ViewReducerState, ActionType] => {
  const {
    activePanelIndex,
    targetPanelIndex,
    isMovingToSameSection,
    isMovingToSamePosition,
    activeSection,
    targetSection,
  } = getMovePanelData(state, active, target);

  // Validate that both panels exist
  if (activePanelIndex === -1 || targetPanelIndex === -1) {
    console.warn('movePanelAction: Panel not found', {
      activePanelRefId: active.panelRef.id,
      targetPanelRefId: target.panelRef?.id,
      activeIndex: activePanelIndex,
      targetIndex: targetPanelIndex,
    });
    return [state, Actions.noOp(ref)];
  }

  // If moving to the same section and position, no-op
  if (isMovingToSameSection && isMovingToSamePosition) {
    return [state, Actions.noOp(ref)];
  }

  let newState: ViewReducerState;

  // Grab these values before any state updates so we can use them in undo action
  const prevPanelIsAuto =
    state.parts[active.panelRef.type][active.panelRef.id].isAuto;
  const prevActiveSectionIsPanelsAuto = activeSection.isPanelsAuto;
  const prevTargetSectionIsPanelsAuto = targetSection.isPanelsAuto;

  const activeData = {
    panelIndex: activePanelIndex,
    panelRef: active.panelRef,
    sectionRef: active.sectionRef,
    section: activeSection,
  };

  const targetData = {
    panelIndex: targetPanelIndex,
    insertDir: target.insertDir,
    panelRef: target.panelRef,
    sectionRef: target.sectionRef,
    section: targetSection,
  };

  if (!isMovingToSameSection) {
    newState = handleMovePanelToDiffSection(state, activeData, targetData);
  } else {
    newState = handleMovePanelToSameSection(state, activeData, targetData);
  }

  const undoAction = createUndoMoveActionData(
    ref,
    newState,
    {
      ...activeData,
      prevIsPanelAuto: prevPanelIsAuto,
      prevSectionPanelsAuto: prevActiveSectionIsPanelsAuto,
    },
    {
      ...targetData,
      prevSectionPanelsAuto: prevTargetSectionIsPanelsAuto,
    }
  );

  // Update the active panel's information
  newState = setInShallowClone(
    newState,
    ['parts', active.panelRef.type, active.panelRef.id, 'isAuto'],
    false
  );

  newState = setInShallowClone(
    newState,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'isPanelsAuto'],
    false
  );

  if (!isMovingToSameSection) {
    newState = setInShallowClone(
      newState,
      ['parts', target.sectionRef.type, target.sectionRef.id, 'isPanelsAuto'],
      false
    );
  }

  // Update the section's information
  // set manual sorting to section chart was moved to
  // TODO: improve this by checking if the panel was placed
  // in a position respecting or automatically placing it in
  // the sorted position and showing a tool tip
  newState = setInShallowClone(
    newState,
    ['parts', active.sectionRef.type, active.sectionRef.id, 'sorted'],
    SectionPanelSorting.Manual
  );

  return [newState, undoAction];
};

const createUndoMoveActionData = (
  ref: Types.Ref,
  newState: ViewReducerState,
  active: ActiveData & {
    prevIsPanelAuto: boolean | undefined;
    prevSectionPanelsAuto: boolean | undefined;
  },
  target: TargetData & {
    prevSectionPanelsAuto: boolean | undefined;
  }
) => {
  const {undoTargetPanelRef, undoInsertDir} = getUndoData(newState, active);

  // For undo, we need to move the panel from its new location back to its original location
  // If there's a panel at the original position, use it as the target
  // Otherwise, if the section is empty, we'll need to handle that case
  return Actions.undoMovePanel2(
    ref,
    {
      panelRef: active.panelRef, // The panel we just moved
      sectionRef: target.sectionRef, // It's now in the target section
      prevIsPanelAuto: active.prevIsPanelAuto,
      prevSectionPanelsAuto: target.prevSectionPanelsAuto,
    },
    {
      // If section is empty, we'll move to the first position (using the panel itself as target)
      insertDir: undoInsertDir,
      panelRef: undoTargetPanelRef, // The panel now at the original position
      sectionRef: active.sectionRef, // In the original section
      prevSectionPanelsAuto: active.prevSectionPanelsAuto,
    }
  );
};
